"use client";

import { useEffect, useRef } from "react";
import { gsap } from "gsap";

interface CursorProps {
  containerRef: React.RefObject<HTMLElement | null>;
}

export function Cursor({ containerRef }: CursorProps) {
  const glowRef = useRef<HTMLDivElement>(null);
  const posRef = useRef({ x: 0, y: 0 });
  const visibleRef = useRef(false);

  useEffect(() => {
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    const canHover = window.matchMedia("(hover: hover)").matches;
    const node = containerRef.current;
    const glow = glowRef.current;

    if (reduced || !canHover || !node || !glow) return;

    function handleMove(e: MouseEvent) {
      if (!glow) return;

      posRef.current = { x: e.clientX, y: e.clientY };

      if (!visibleRef.current) {
        visibleRef.current = true;
        gsap.to(glow, { opacity: 1, duration: 0.4 });
      }

      gsap.to(glow, {
        left: e.clientX,
        top: e.clientY,
        duration: 0.6,
        ease: "power2.out",
        overwrite: "auto",
      });
    }

    function handleLeave() {
      if (!glow) return;
      visibleRef.current = false;
      gsap.to(glow, { opacity: 0, duration: 0.4 });
    }

    node.addEventListener("mousemove", handleMove);
    node.addEventListener("mouseleave", handleLeave);

    return () => {
      node.removeEventListener("mousemove", handleMove);
      node.removeEventListener("mouseleave", handleLeave);
    };
  }, [containerRef]);

  return (
    <div
      ref={glowRef}
      aria-hidden="true"
      className="pointer-events-none fixed z-[5] h-[480px] w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-0 will-change-[left,top,opacity]"
      style={{
        background:
          "radial-gradient(circle, rgba(217,154,78,0.12) 0%, rgba(217,154,78,0) 70%)",
      }}
    />
  );
}